import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import "../css/ProfileAcademy.css";

const BACKEND_URL = "https://linxlegacyacademy.dk";

const CATEGORY_COLORS = {
  træning:   { color: "#7F77DD", label: "Træning" },
  turnering: { color: "#D85A30", label: "Turnering" },
  møde:      { color: "#1D9E75", label: "Møde" },
  event:     { color: "#378ADD", label: "Event" },
  andet:     { color: "#888780", label: "Andet" },
};

function getCategoryStyle(kategori) {
  const key = (kategori || "andet").toLowerCase();
  return CATEGORY_COLORS[key] || CATEGORY_COLORS["andet"];
}

function CalendarLegend() {
  return (
    <div className="calendar-legend">
      {Object.values(CATEGORY_COLORS).map(({ color, label }) => (
        <div key={label} className="legend-item">
          <span className="legend-dot" style={{ backgroundColor: color }} />
          <span className="legend-label">{label}</span>
        </div>
      ))}
    </div>
  );
}

// -------------------------
// Modal: opret ny begivenhed
// -------------------------
function CreateModal({ date, onClose, onSave }) {
  const [titel, setTitel] = useState("");
  const [kategori, setKategori] = useState("træning");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [beskrivelse, setBeskrivelse] = useState("");

  const handleSave = () => {
    if (!titel.trim()) return;
    onSave({ titel, kategori, start: `${date}T${startTime}`, slut: `${date}T${endTime}`, beskrivelse });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <h3>Ny begivenhed</h3>

        <div className="modal-field">
          <label>Titel</label>
          <input type="text" value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="Tilføj titel..." autoFocus />
        </div>

        <div className="modal-field">
          <label>Dato</label>
          <input type="date" value={date} readOnly />
        </div>

        <div className="modal-row">
          <div className="modal-field">
            <label>Start</label>
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </div>
          <div className="modal-field">
            <label>Slut</label>
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </div>
        </div>

        <div className="modal-field">
          <label>Kategori</label>
          <select value={kategori} onChange={(e) => setKategori(e.target.value)}>
            {Object.entries(CATEGORY_COLORS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        <div className="modal-field">
          <label>Beskrivelse</label>
          <textarea value={beskrivelse} onChange={(e) => setBeskrivelse(e.target.value)} placeholder="Valgfri beskrivelse..." rows={3} />
        </div>

        <div className="modal-actions">
          <button className="btn-modal-cancel" onClick={onClose}>Annuller</button>
          <button className="btn-modal-save" onClick={handleSave} disabled={!titel.trim()}>Gem begivenhed</button>
        </div>
      </div>
    </div>
  );
}

// -------------------------
// Modal: vis/slet begivenhed
// -------------------------
function ViewModal({ event, onClose, onDelete }) {
  const { color, label } = getCategoryStyle(event.extendedProps?.kategori);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-event-color" style={{ background: color }} />
        <h3>{event.title}</h3>

        <div className="modal-field">
          <label>Kategori</label>
          <p>{label}</p>
        </div>

        <div className="modal-row">
          <div className="modal-field">
            <label>Start</label>
            <p>{event.start ? new Date(event.start).toLocaleString("da-DK", { dateStyle: "short", timeStyle: "short" }) : "—"}</p>
          </div>
          <div className="modal-field">
            <label>Slut</label>
            <p>{event.end ? new Date(event.end).toLocaleString("da-DK", { dateStyle: "short", timeStyle: "short" }) : "—"}</p>
          </div>
        </div>

        {event.extendedProps?.beskrivelse && (
          <div className="modal-field">
            <label>Beskrivelse</label>
            <p>{event.extendedProps.beskrivelse}</p>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-modal-delete" onClick={() => onDelete(event.extendedProps?.dbId)}>Slet</button>
          <button className="btn-modal-cancel" onClick={onClose}>Luk</button>
        </div>
      </div>
    </div>
  );
}

export default function ProfileAcademy() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState({});
  const [holdInfo, setHoldInfo] = useState(null);
  const [createDate, setCreateDate] = useState(null);
  const [viewEvent, setViewEvent] = useState(null);
  const navigate = useNavigate();

  const editableFields = ["email", "phone", "ingame_name"];
  const personalFields = {
    personal_id: "Personlige ID",
    discord_id: "Discord ID",
    username: "Brugernavn",
    email: "E-mail",
    phone: "Telefon",
    ingame_name: "Ingame Navn",
  };

  useEffect(() => {
    async function fetchUser() {
      try {
        const res = await fetch(`${BACKEND_URL}/api/profile/user`, { credentials: "include" });
        const data = await res.json();
        if (data.status === "logged_in") {
          setUser(data);
          setEditData({ ...data });
        } else {
          navigate("/login");
        }
      } catch (err) {
        console.error("Fejl ved hentning af bruger:", err);
        navigate("/login");
      } finally {
        setLoading(false);
      }
    }

    async function fetchHoldInfo() {
      try {
        const res = await fetch(`${BACKEND_URL}/api/profile/user/holdinfo`, { credentials: "include" });
        const data = await res.json();
        setHoldInfo(data);
      } catch (err) {
        console.error("Fejl ved hentning af hold info:", err);
        setHoldInfo({ status: "error" });
      }
    }

    fetchUser();
    fetchHoldInfo();
  }, [navigate]);

  const saveProfile = async () => {
    if (!user) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/profile/user`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editData),
      });
      const updated = await res.json();
      if (updated.status === "success") {
        setUser({ ...user, ...updated });
        alert("Profil opdateret!");
      } else {
        alert("Kunne ikke opdatere profil: " + updated.status);
      }
    } catch (err) {
      alert("Kunne ikke opdatere profil.");
    }
  };

  const payPackage = async () => {
    if (!user?.stripe_price_id) return alert("Ingen pakke valgt");
    try {
      const res = await fetch(`${BACKEND_URL}/api/profile/user/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ price_id: user.stripe_price_id }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else alert("Der skete en fejl ved Stripe checkout.");
    } catch (err) {
      alert("Kunne ikke starte betaling.");
    }
  };

  // Klik på dato → opret modal
  const handleDateClick = (info) => {
    setCreateDate(info.dateStr);
  };

  // Klik på event → vis modal
  const handleEventClick = (info) => {
    setViewEvent(info.event);
  };

  // Træk event til ny dato/tid
  const handleEventDrop = async (info) => {
    const dbId = info.event.extendedProps?.dbId;
    if (!dbId) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/events/${dbId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          start: info.event.start?.toISOString(),
          slut: info.event.end?.toISOString() || info.event.start?.toISOString(),
        }),
      });
      const result = await res.json();
      if (result.status !== "success") {
        info.revert();
        alert("Kunne ikke flytte begivenhed.");
      }
    } catch (err) {
      info.revert();
      alert("Kunne ikke flytte begivenhed.");
    }
  };

  // Resize event (træk i bunden)
  const handleEventResize = async (info) => {
    const dbId = info.event.extendedProps?.dbId;
    if (!dbId) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/events/${dbId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          start: info.event.start?.toISOString(),
          slut: info.event.end?.toISOString(),
        }),
      });
      const result = await res.json();
      if (result.status !== "success") {
        info.revert();
        alert("Kunne ikke ændre begivenhed.");
      }
    } catch (err) {
      info.revert();
    }
  };

  // Gem ny begivenhed
  const handleSaveEvent = async (eventData) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/events`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(eventData),
      });
      const result = await res.json();
      if (result.status === "success") {
        setHoldInfo((prev) => ({
          ...prev,
          events: [...(prev.events || []), { ...eventData, id: result.id }],
        }));
      } else {
        alert("Kunne ikke gemme begivenhed: " + result.message);
      }
    } catch (err) {
      alert("Kunne ikke gemme begivenhed.");
    }
    setCreateDate(null);
  };

  // Slet begivenhed
  const handleDeleteEvent = async (dbId) => {
    if (!dbId) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/events/${dbId}`, {
        method: "DELETE",
        credentials: "include",
      });
      const result = await res.json();
      if (result.status === "success") {
        setHoldInfo((prev) => ({
          ...prev,
          events: prev.events.filter((e) => e.id !== dbId),
        }));
        setViewEvent(null);
      } else {
        alert("Kunne ikke slette: " + result.message);
      }
    } catch (err) {
      alert("Kunne ikke slette begivenhed.");
    }
  };

  const mappedEvents = (holdInfo?.events || []).map((ev) => {
    const { color } = getCategoryStyle(ev.kategori);
    return {
      id: String(ev.id),
      title: ev.titel,
      start: ev.start,
      end: ev.slut,
      backgroundColor: color,
      borderColor: color,
      extendedProps: {
        dbId: ev.id,
        kategori: ev.kategori || "andet",
        beskrivelse: ev.beskrivelse || "",
      },
    };
  });

  if (loading) return <p className="loading-text">Indlæser...</p>;
  if (!user) return null;

  return (
    <div className="profile-container">

      {/* Opret modal */}
      {createDate && (
        <CreateModal
          date={createDate}
          onClose={() => setCreateDate(null)}
          onSave={handleSaveEvent}
        />
      )}

      {/* Vis/slet modal */}
      {viewEvent && (
        <ViewModal
          event={viewEvent}
          onClose={() => setViewEvent(null)}
          onDelete={handleDeleteEvent}
        />
      )}

      {/* Header */}
      <div className="profile-header">
        <h1 className="welcome-text">Velkommen - {user.username}</h1>
      </div>

      {/* Personlige oplysninger */}
      <div className="profile-card">
        <h2>Personlige oplysninger</h2>
        {Object.keys(personalFields).map((key) => (
          <div key={key} className="form-group">
            <label>{personalFields[key]}</label>
            <input
              type="text"
              value={editData[key] || ""}
              readOnly={!editableFields.includes(key)}
              onChange={(e) => {
                if (editableFields.includes(key)) setEditData({ ...editData, [key]: e.target.value });
              }}
              className={!editableFields.includes(key) ? "readonly-input" : ""}
            />
          </div>
        ))}
        <button className="btn btn-save" onClick={saveProfile}>Gem ændringer</button>
      </div>

      {/* Hold Info */}
      <div className="profile-card">
        <h2>Hold Info</h2>
        {holdInfo ? (
          holdInfo.status === "success" ? (
            <>
              <p>Holdnavn: {holdInfo.holdnavn}</p>
              <h3>Mine spil tider</h3>
              {holdInfo.game_times?.length > 0 ? (
                <ul>
                  {holdInfo.game_times.map((gt, idx) => (
                    <li key={idx}>{gt.game_name} - {gt.day_name} {gt.start_time} til {gt.end_time}</li>
                  ))}
                </ul>
              ) : <p>Ingen spil tider registreret</p>}
            </>
          ) : holdInfo.status === "no_team" ? (
            <p>Du er ikke tilknyttet et hold.</p>
          ) : <p>Kunne ikke hente hold info fra serveren.</p>
        ) : <p>Indlæser hold info...</p>}
      </div>

      {/* Pakke */}
      <div className="profile-card package-card">
        <div className="package-header">
          <h2>{user.stripe_product_name || "Support"}</h2>
          {user.payment_status && (() => {
            const statusMap = {
              active:     { label: "Betalt ✅",                className: "paid" },
              paid:       { label: "Betalt ✅",                className: "paid" },
              past_due:   { label: "Betaling fejlet ⚠️",       className: "past_due" },
              canceled:   { label: "Annulleret ❌",            className: "canceled" },
              manual:     { label: "Manuel behandling 🕒",     className: "manual" },
              incomplete: { label: "Ufuldstændig betaling ⚠️", className: "incomplete" },
              trialing:   { label: "Prøveperiode ⏳",          className: "trialing" },
              unpaid:     { label: "Ikke betalt ❌",           className: "unpaid" },
            };
            const { label, className } = statusMap[user.payment_status] || { label: "Ikke betalt ❌", className: "unpaid" };
            return <span className={`package-status ${className}`}>{label}</span>;
          })()}
        </div>
        {user.stripe_price_id && user.payment_status !== "paid" && (
          <button className="btn-pay" onClick={payPackage}>Betal pakke</button>
        )}
        <ul className="package-features">
          <li>🎮 Adgang til holdtræning</li>
          <li>💬 Direkte support</li>
          <li>📅 Eksklusive events</li>
        </ul>
      </div>

      {/* Hold Kalender */}
      {holdInfo?.status === "success" && (
        <div className="profile-card calendar-card">
          <div className="calendar-header-row">
            <h2>Hold Kalender</h2>
            <span className="calendar-hint">Klik på dato for at oprette • Klik på event for detaljer</span>
          </div>

          <CalendarLegend />

          <div className="calendar-container">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={window.innerWidth < 768 ? "timeGridWeek" : "dayGridMonth"}
              headerToolbar={{
                left: "prev,next",
                center: "title",
                right: "dayGridMonth,timeGridWeek",
              }}
              height="auto"
              locale="da"
              selectable={true}
              editable={true}
              droppable={true}
              dateClick={handleDateClick}
              eventClick={handleEventClick}
              eventDrop={handleEventDrop}
              eventResize={handleEventResize}
              events={mappedEvents}
              eventDidMount={(info) => {
                const { kategori } = info.event.extendedProps;
                const { label } = getCategoryStyle(kategori);
                const badge = document.createElement("span");
                badge.style.cssText = "display:block;font-size:10px;opacity:0.85;margin-top:1px;font-weight:400;";
                badge.textContent = label;
                info.el.querySelector(".fc-event-title")?.after(badge);
              }}
            />
          </div>
        </div>
      )}

    </div>
  );
}